import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_SALES_DEAL, DELETE_SALES_DEAL, UPDATE_SALES_DEAL_STATUS, VALIDATE_FICA } from '../../graphql/sales-deals';
import DealWorkflowPanel from './DealWorkflowPanel';

const SalesDealsDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusAction, setStatusAction] = useState('');
    const [statusReason, setStatusReason] = useState('');

    const { loading, error, data, refetch } = useQuery(GET_SALES_DEAL, {
        variables: { id },
        fetchPolicy: 'network-only'
    });

    const [deleteSalesDeal] = useMutation(DELETE_SALES_DEAL, {
        onCompleted: () => navigate('/sales/deals'),
        onError: error => {
            console.error('Error deleting sales deal:', error);
            alert('Failed to delete sales deal. Please try again.');
        }
    });

    const [updateStatus] = useMutation(UPDATE_SALES_DEAL_STATUS, {
        onCompleted: () => {
            refetch();
            setShowStatusModal(false);
            setStatusReason('');
        },
        onError: (error) => {
            console.error('Error updating status:', error);
            alert('Failed to update status. Please try again.');
        }
    });

    const [validateFica] = useMutation(VALIDATE_FICA, {
        onCompleted: () => {
            refetch();
        },
        onError: (error) => {
            console.error('Error validating FICA:', error);
            alert('Failed to validate FICA. Please try again.');
        }
    });

    const handleDelete = () => {
        if (window.confirm(`Are you sure you want to delete deal ${data?.salesDeal?.dealNumber || 'this sales deal'}? This action cannot be undone.`)) {
            deleteSalesDeal({ variables: { id } });
        }
    };

    const handleStatusChange = (action) => {
        setStatusAction(action);
        setStatusReason('');
        setShowStatusModal(true);
    };

    const handleStatusSubmit = () => {
        if (statusAction === 'FAILED' && !statusReason.trim()) {
            alert('Please provide a reason for marking the deal as failed.');
            return;
        }

        updateStatus({
            variables: {
                id,
                status: statusAction,
                statusData: statusReason || null
            }
        });
    };

    const handleFicaValidation = () => {
        if (window.confirm('Mark FICA as checked and validated?')) {
            validateFica({ variables: { dealId: id } });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Invalid date format';
            }
            return date.toLocaleDateString('en-ZA', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateString);
            return 'Invalid date';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const getStatusColor = (status) => {
        const colors = {
            PENDING: 'bg-yellow-100 text-yellow-800',
            CONCLUDED: 'bg-blue-100 text-blue-800',
            COMPLETED: 'bg-green-100 text-green-800',
            FAILED: 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getPropertyTypeLabel = (type) => {
        const types = {
            HOUSE: 'House',
            APARTMENT: 'Apartment',
            TOWNHOUSE: 'Townhouse',
            FLAT: 'Flat',
            DUPLEX: 'Duplex',
            VACANT_LAND: 'Vacant Land',
            COMMERCIAL: 'Commercial',
            FARM: 'Farm'
        };
        return types[type] || type;
    };

    const getRoleLabel = (role) => {
        const roles = {
            PRIMARY: 'Primary Agent',
            SECONDARY: 'Secondary Agent',
            REFERRAL: 'Referral Agent'
        };
        return roles[role] || role;
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-300 rounded w-1/3 mb-4"></div>
                    <div className="space-y-3">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="h-4 bg-gray-300 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-red-800 font-medium">Error loading sales deal</div>
                    <div className="text-red-600 text-sm mt-1">{error.message}</div>
                </div>
            </div>
        );
    }

    if (!data?.salesDeal) {
        return (
            <div className="p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="text-yellow-800 font-medium">Sales deal not found</div>
                    <div className="text-yellow-600 text-sm mt-1">The requested sales deal could not be found.</div>
                </div>
            </div>
        );
    }

    const deal = data.salesDeal;

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-semibold flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Sales Deal Details
                </h1>
                <Link to="/sales/deals" className="flex items-center text-blue-600 hover:text-blue-800">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Sales Deals
                </Link>
            </div>

            {/* Action Buttons */}
            <div className="mb-6 flex flex-wrap gap-3">
                <Link 
                    to={`/sales/deals/${deal.id}/edit`} 
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                </Link>

                {/* Status Actions */}
                {deal.status === 'PENDING' && (
                    <>
                        <button
                            onClick={() => handleStatusChange('CONCLUDED')}
                            className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Mark as Concluded
                        </button>
                        <button
                            onClick={() => handleStatusChange('FAILED')}
                            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Mark as Failed
                        </button>
                    </>
                )}

                {deal.status === 'CONCLUDED' && (
                    <>
                        <button
                            onClick={() => handleStatusChange('COMPLETED')}
                            className="inline-flex items-center px-4 py-2 border border-green-300 rounded-md shadow-sm text-sm font-medium text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Mark as Completed
                        </button>
                        <button
                            onClick={() => handleStatusChange('FAILED')}
                            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Mark as Failed
                        </button>
                    </>
                )}

                {!deal.ficaChecked && (
                    <button
                        onClick={handleFicaValidation}
                        className="inline-flex items-center px-4 py-2 border border-purple-300 rounded-md shadow-sm text-sm font-medium text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Validate FICA
                    </button>
                )}

                <button
                    onClick={handleDelete}
                    className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                </button>
            </div>

            {/* Status & Suspensive Conditions workflow */}
            <DealWorkflowPanel deal={deal} refetch={refetch} />

            {/* Deal Summary Card */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                    <div className="flex items-center">
                        <div className="bg-blue-100 text-blue-700 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold mr-4">
                            {deal.dealNumber?.substring(0, 2) || 'SD'}
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900">{deal.dealNumber}</h3>
                            <p className="mt-1 text-sm text-gray-500">{deal.streetAddress}</p>
                            <p className="text-sm text-gray-500">{deal.suburb}, {deal.city}</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-green-600 mb-1">
                            {formatCurrency(deal.sellingPrice)}
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(deal.status)}`}>
                            {deal.status}
                        </span>
                    </div>
                </div>
            </div>

            {/* Timeline Information */}
            {(deal.otpDate || deal.registrationDate || deal.daysSinceOTP) && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Deal Timeline</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">Transaction progress from OTP to registration</p>
                    </div>
                    <div className="border-t border-gray-200">
                        <dl>
                            {deal.otpDate && (
                                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">OTP Date</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {formatDate(deal.otpDate)}
                                    </dd>
                                </div>
                            )}
                            {deal.registrationDate && (
                                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Registration Date</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {formatDate(deal.registrationDate)}
                                    </dd>
                                </div>
                            )}
                            {deal.daysFromOTPToRegistration !== null && deal.daysFromOTPToRegistration !== undefined && (
                                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Time to Registration</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {deal.daysFromOTPToRegistration} days
                                        </span>
                                    </dd>
                                </div>
                            )}
                            {deal.daysSinceOTP !== null && deal.daysSinceOTP !== undefined && !deal.registrationDate && (
                                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Days Since OTP</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            {deal.daysSinceOTP} days (In Progress)
                                        </span>
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>
            )}

            {/* Property Information */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Property Information</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Details about the property being sold</p>
                </div>
                <div className="border-t border-gray-200">
                    <dl>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Full Address</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {deal.streetAddress}<br />
                                {deal.suburb}, {deal.city} {deal.postalCode}
                                {deal.erfNumber && <><br />ERF: {deal.erfNumber}</>}
                            </dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Property Type</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getPropertyTypeLabel(deal.propertyType)}
                            </dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Property Features</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {[
                                    deal.bedrooms && `${deal.bedrooms} Bedrooms`,
                                    deal.bathrooms && `${deal.bathrooms} Bathrooms`,
                                    deal.garages && `${deal.garages} Garages`
                                ].filter(Boolean).join(' • ') || 'Not specified'}
                            </dd>
                        </div>
                        {deal.propertyDescription && (
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Description</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {deal.propertyDescription}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>

            {/* Sellers & Purchasers */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Sellers & Purchasers</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Parties involved in the transaction</p>
                </div>
                <div className="border-t border-gray-200">
                    {/* Sellers */}
                    <div className="bg-gray-50 px-4 py-4">
                        <h4 className="text-md font-medium text-gray-900 mb-3">Sellers</h4>
                        {deal.sellers && deal.sellers.length > 0 ? (
                            <div className="space-y-4">
                                {deal.sellers.map((seller, index) => (
                                    <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <div className="text-sm font-medium text-gray-500">
                                                    {seller.isEntity ? 'Entity Name' : 'Name'}
                                                </div>
                                                <div className="mt-1 text-sm text-gray-900 font-semibold">
                                                    {seller.name}
                                                    {seller.isEntity && (
                                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                            Entity
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {seller.email && (
                                                <div>
                                                    <div className="text-sm font-medium text-gray-500">Email</div>
                                                    <div className="mt-1 text-sm text-gray-900">{seller.email}</div>
                                                </div>
                                            )}
                                            {seller.phone && (
                                                <div>
                                                    <div className="text-sm font-medium text-gray-500">Phone</div>
                                                    <div className="mt-1 text-sm text-gray-900">{seller.phone}</div>
                                                </div>
                                            )}
                                            {seller.idNumber && (
                                                <div>
                                                    <div className="text-sm font-medium text-gray-500">
                                                        {seller.isEntity ? 'Registration Number' : 'ID Number'}
                                                    </div>
                                                    <div className="mt-1 text-sm text-gray-900">{seller.idNumber}</div>
                                                </div>
                                            )}
                                        </div>
                                        {seller.isEntity && seller.representativeName && (
                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                                <div className="text-sm font-medium text-gray-700 mb-2">Representative Details</div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-500">Name</div>
                                                        <div className="mt-1 text-sm text-gray-900">{seller.representativeName}</div>
                                                    </div>
                                                    {seller.representativeEmail && (
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-500">Email</div>
                                                            <div className="mt-1 text-sm text-gray-900">{seller.representativeEmail}</div>
                                                        </div>
                                                    )}
                                                    {seller.representativePhone && (
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-500">Phone</div>
                                                            <div className="mt-1 text-sm text-gray-900">{seller.representativePhone}</div>
                                                        </div>
                                                    )}
                                                    {seller.representativeIdNumber && (
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-500">ID Number</div>
                                                            <div className="mt-1 text-sm text-gray-900">{seller.representativeIdNumber}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">No sellers specified</div>
                        )}
                    </div>

                    {/* Purchasers */}
                    <div className="bg-white px-4 py-4">
                        <h4 className="text-md font-medium text-gray-900 mb-3">Purchasers</h4>
                        {deal.purchasers && deal.purchasers.length > 0 ? (
                            <div className="space-y-4">
                                {deal.purchasers.map((purchaser, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <div className="text-sm font-medium text-gray-500">
                                                    {purchaser.isEntity ? 'Entity Name' : 'Name'}
                                                </div>
                                                <div className="mt-1 text-sm text-gray-900 font-semibold">
                                                    {purchaser.name}
                                                    {purchaser.isEntity && (
                                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                                            Entity
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {purchaser.email && (
                                                <div>
                                                    <div className="text-sm font-medium text-gray-500">Email</div>
                                                    <div className="mt-1 text-sm text-gray-900">{purchaser.email}</div>
                                                </div>
                                            )}
                                            {purchaser.phone && (
                                                <div>
                                                    <div className="text-sm font-medium text-gray-500">Phone</div>
                                                    <div className="mt-1 text-sm text-gray-900">{purchaser.phone}</div>
                                                </div>
                                            )}
                                            {purchaser.idNumber && (
                                                <div>
                                                    <div className="text-sm font-medium text-gray-500">
                                                        {purchaser.isEntity ? 'Registration Number' : 'ID Number'}
                                                    </div>
                                                    <div className="mt-1 text-sm text-gray-900">{purchaser.idNumber}</div>
                                                </div>
                                            )}
                                        </div>
                                        {purchaser.isEntity && purchaser.representativeName && (
                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                                <div className="text-sm font-medium text-gray-700 mb-2">Representative Details</div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-500">Name</div>
                                                        <div className="mt-1 text-sm text-gray-900">{purchaser.representativeName}</div>
                                                    </div>
                                                    {purchaser.representativeEmail && (
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-500">Email</div>
                                                            <div className="mt-1 text-sm text-gray-900">{purchaser.representativeEmail}</div>
                                                        </div>
                                                    )}
                                                    {purchaser.representativePhone && (
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-500">Phone</div>
                                                            <div className="mt-1 text-sm text-gray-900">{purchaser.representativePhone}</div>
                                                        </div>
                                                    )}
                                                    {purchaser.representativeIdNumber && (
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-500">ID Number</div>
                                                            <div className="mt-1 text-sm text-gray-900">{purchaser.representativeIdNumber}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">No purchasers specified</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Financial Information */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Financial Information</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Pricing and commission details</p>
                </div>
                <div className="border-t border-gray-200">
                    <dl>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Selling Price</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <span className="text-lg font-semibold text-green-600">{formatCurrency(deal.sellingPrice)}</span>
                            </dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Commission Input Method</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    deal.commissionInputMethod === 'PERCENTAGE' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                }`}>
                                    {deal.commissionInputMethod === 'PERCENTAGE' ? 'Percentage' : 'Amount'}
                                </span>
                            </dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Commission</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <div className="space-y-1">
                                    <div>
                                        <span className="font-medium">{deal.totalCommissionPercentage?.toFixed(2)}%</span>
                                        {' = '}
                                        <span className="font-medium">{formatCurrency(deal.totalCommissionExclusiveVAT)}</span>
                                        {' '}
                                        <span className="text-xs text-gray-500">(Excl. VAT)</span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Including VAT: {formatCurrency(deal.totalCommissionInclusiveVAT)}
                                    </div>
                                </div>
                            </dd>
                        </div>
                        {/* Payment Method */}
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    deal.paymentMethod === 'CASH' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {deal.paymentMethod === 'CASH' ? 'Cash' : 'Bond'}
                                </span>
                            </dd>
                        </div>

                        {/* Cash Payment Details */}
                        {deal.paymentMethod === 'CASH' && deal.cashPayableDate && (
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Cash Payable Date</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {formatDate(deal.cashPayableDate)}
                                </dd>
                            </div>
                        )}

                        {/* Bond Payment Details */}
                        {deal.paymentMethod === 'BOND' && (
                            <>
                                {deal.bondAmount && (
                                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-sm font-medium text-gray-500">Bond Amount</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            <span className="text-lg font-semibold">{formatCurrency(deal.bondAmount)}</span>
                                        </dd>
                                    </div>
                                )}
                                {deal.bondGrantDate && (
                                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-sm font-medium text-gray-500">Bond Grant Date (Suspensive Condition)</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {formatDate(deal.bondGrantDate)}
                                        </dd>
                                    </div>
                                )}
                            </>
                        )}
                        {deal.depositAmount && (
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Deposit</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {formatCurrency(deal.depositAmount)}
                                    {deal.depositDueDate && (
                                        <span className="text-xs text-gray-500 block">Due: {formatDate(deal.depositDueDate)}</span>
                                    )}
                                    {deal.depositReceived && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                                            Received {deal.depositReceivedDate && `on ${formatDate(deal.depositReceivedDate)}`}
                                        </span>
                                    )}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>

            {/* Agents & Commission */}
            {(() => {
                const totalCommission = deal.totalCommissionExclusiveVAT || 0;
                const referralPct = (deal.hasReferral && deal.referralInfo?.commissionPercentage) || 0;
                const referralAmount = totalCommission * referralPct / 100;
                const postReferral = totalCommission - referralAmount;
                const totalAgentSharePct = (deal.agents || []).reduce(
                    (sum, a) => sum + (a.commissionPercentage || 0),
                    0
                );
                const totalAgentAmount = postReferral * totalAgentSharePct / 100;
                const companyAmount = postReferral - totalAgentAmount;
                const companyPct = 100 - totalAgentSharePct;
                return (
                    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                        <div className="px-4 py-5 sm:px-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">Agents & Commission</h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">Agents involved and commission structure</p>
                        </div>
                        <div className="border-t border-gray-200">
                            {deal.agents && deal.agents.length > 0 ? (
                                deal.agents.map((agent, index) => (
                                    <div key={agent.agentId.id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} px-4 py-5`}>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {agent.agentId.firstName} {agent.agentId.lastName}
                                                </div>
                                                <div className="text-sm text-gray-500">{agent.agentId.email}</div>
                                            </div>
                                            <div>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {getRoleLabel(agent.role)}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-lg font-semibold text-gray-900">
                                                    {agent.commissionPercentage}%
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm text-gray-900">
                                                    {formatCurrency(postReferral * (agent.commissionPercentage || 0) / 100)}
                                                </div>
                                                <div className="text-xs text-gray-500">Commission Amount</div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="px-4 py-5 text-center text-gray-500">
                                    No agents assigned to this deal
                                </div>
                            )}

                            {/* Referral payout (if any) */}
                            {referralPct > 0 && (
                                <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                                        <div className="md:col-span-2">
                                            <div className="font-medium text-gray-900">
                                                Referral payout
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {deal.referralInfo?.company || 'External referral'} — taken off the top
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-semibold text-gray-900">
                                                {referralPct.toFixed(2)}%
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm text-gray-900">
                                                {formatCurrency(referralAmount)}
                                            </div>
                                            <div className="text-xs text-gray-500">of total commission</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Company portion (whatever's left) */}
                            <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                                    <div className="md:col-span-2">
                                        <div className="font-medium text-gray-900">Company portion</div>
                                        <div className="text-xs text-gray-500">Remainder after referral and agents</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-semibold text-gray-900">
                                            {companyPct.toFixed(2)}%
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm text-gray-900">
                                            {formatCurrency(companyAmount)}
                                        </div>
                                        <div className="text-xs text-gray-500">of post-referral pool</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* External Referral */}
            {deal.hasReferral && deal.referralInfo && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">External Referral</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">Referral source information</p>
                    </div>
                    <div className="border-t border-gray-200">
                        <dl>
                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Company</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {deal.referralInfo.company}
                                </dd>
                            </div>
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">External Agent</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <div>{deal.referralInfo.agentName}</div>
                                    {deal.referralInfo.agentEmail && (
                                        <div className="text-xs text-gray-500">Email: {deal.referralInfo.agentEmail}</div>
                                    )}
                                    {deal.referralInfo.agentPhone && (
                                        <div className="text-xs text-gray-500">Phone: {deal.referralInfo.agentPhone}</div>
                                    )}
                                </dd>
                            </div>
                            {deal.referralInfo.referralNumber && (
                                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Referral Number</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {deal.referralInfo.referralNumber}
                                    </dd>
                                </div>
                            )}
                            {deal.referralInfo.commissionPercentage && (
                                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Commission Percentage</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {deal.referralInfo.commissionPercentage}%
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>
            )}

            {/* Professional Services */}
            {(deal.transferAttorney || deal.bondOriginator || deal.bondAttorney) && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Professional Services</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">Legal and financial services</p>
                    </div>
                    <div className="border-t border-gray-200">
                        <dl>
                            {[
                                { label: 'Transfer Attorney', assignment: deal.transferAttorney, stripe: 'bg-gray-50' },
                                { label: 'Bond Originator', assignment: deal.bondOriginator, stripe: 'bg-white' },
                                { label: 'Bond Attorney', assignment: deal.bondAttorney, stripe: 'bg-gray-50' }
                            ].filter((row) => row.assignment).map(({ label, assignment, stripe }) => {
                                const svc = assignment.professionalServiceId || {};
                                const pc = svc.primaryContact || {};
                                const fullName = [pc.title, pc.firstName, pc.lastName].filter(Boolean).join(' ');
                                return (
                                    <div key={label} className={`${stripe} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                                        <dt className="text-sm font-medium text-gray-500">{label}</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 space-y-1">
                                            <div className="font-medium">{svc.companyName || '—'}</div>
                                            {fullName && (
                                                <div className="text-xs text-gray-700">Contact: {fullName}</div>
                                            )}
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                                                {pc.email && (
                                                    <a href={`mailto:${pc.email}`} className="text-blue-600 hover:underline">
                                                        ✉ {pc.email}
                                                    </a>
                                                )}
                                                {pc.cellphone && (
                                                    <a href={`tel:${pc.cellphone}`} className="text-blue-600 hover:underline">
                                                        📱 {pc.cellphone}
                                                    </a>
                                                )}
                                                {pc.telephone && (
                                                    <a href={`tel:${pc.telephone}`} className="text-blue-600 hover:underline">
                                                        ☎ {pc.telephone}
                                                    </a>
                                                )}
                                            </div>
                                            {assignment.assignedDate && (
                                                <div className="text-xs text-gray-500">Assigned: {formatDate(assignment.assignedDate)}</div>
                                            )}
                                        </dd>
                                    </div>
                                );
                            })}
                        </dl>
                    </div>
                </div>
            )}

            {/* Suspensive Conditions */}
            {deal.suspensiveConditions && deal.suspensiveConditions.length > 0 && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Suspensive Conditions</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">Conditions that must be met for deal completion</p>
                    </div>
                    <div className="border-t border-gray-200">
                        {deal.suspensiveConditions.map((condition, index) => (
                            <div key={condition.id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} px-4 py-4`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="font-medium text-gray-900">{condition.description}</div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Due: {formatDate(condition.dueDate)}
                                        </div>
                                        {condition.notes && (
                                            <div className="text-sm text-gray-600 mt-1">{condition.notes}</div>
                                        )}
                                    </div>
                                    <div className="ml-4">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            condition.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {condition.completed ? 'Completed' : 'Pending'}
                                        </span>
                                        {condition.completed && condition.completedDate && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                Completed: {formatDate(condition.completedDate)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Notes */}
            {deal.notes && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Notes</h3>
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                        <p className="text-sm text-gray-900">{deal.notes}</p>
                    </div>
                </div>
            )}

            {/* System Info */}
            <div className="mt-4 text-sm text-gray-500">
                <p>
                    Created: {formatDate(deal.dateCreated)} by {deal.createdBy?.firstName} {deal.createdBy?.lastName}
                    {deal.updatedAt && <span> • Last Updated: {formatDate(deal.updatedAt)} by {deal.updatedBy?.firstName} {deal.updatedBy?.lastName}</span>}
                </p>
            </div>

            {/* Status Change Modal */}
            {showStatusModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Mark Deal as {statusAction}
                            </h3>
                            
                            {statusAction === 'FAILED' && (
                                <div className="mb-4">
                                    <label htmlFor="statusReason" className="block text-sm font-medium text-gray-700 mb-2">
                                        Reason for failure*
                                    </label>
                                    <textarea
                                        id="statusReason"
                                        rows={3}
                                        value={statusReason}
                                        onChange={(e) => setStatusReason(e.target.value)}
                                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Please provide a reason for marking this deal as failed..."
                                    />
                                </div>
                            )}
                            
                            <div className="flex items-center justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowStatusModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleStatusSubmit}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesDealsDetails;